import './utilities/error-logger/init';
import {render} from 'preact';
import {unmountComponentAtNode} from 'preact/compat';

import {mountWebComponent} from 'utilities/mount';
import {getStyleTags} from 'utilities/shadow-style';
import {App} from 'foundation/App';
import {
  DeliveryEstimatesRequestParameters,
  deliveryPromiseAPIClient,
  hydratedVariantId,
} from 'utilities/delivery-promise';
import {fetchProductDetails, resolveSellingPlanId} from 'utilities/product';
import {getStorage} from 'utilities/dom';
import {readShopifyFeatures} from 'utilities/shopify-features';
import {ShopPromisePerformance} from 'utilities/telemetry/metrics';
import {trackDeliveryPromiseLoaded} from 'utilities/tracking';

declare global {
  interface NodeModule {
    hot: {
      accept(
        dependencies: string[],
        callback: (updatedDependencies: string[]) => void,
      ): void;
      accept(dependency: string, callback: () => void): void;
      accept(errHandler?: (err: any) => void): void;
    };
  }
}

interface CustomElement {
  connectedCallback(): void;
}

enum DeliveryPromiseAttribute {
  VariantId = 'variant-id',
  SellingPlanId = 'selling-plan-id',
}

const tag = 'delivery-promise-wc';

window.Shopify = window.Shopify || {};
window.Shopify.ShopPromise = window.Shopify.ShopPromise || {};
window.Shopify.ShopPromise.mounted =
  window.Shopify.ShopPromise.mounted || false;

/**
 * This is a small, temp fix to a larger change that will come.
 *
 * In the future, we will replace this low-level function, called
 * by clients, with an abstracted function where the params
 * won't need to be passed in. Since this repo has all the
 * necessary params, it's error prone to have clients keep track
 * of all values and store them separately.
 */
window.Shopify.ShopPromise.deliveryPromiseAPIClient = function (
  deliveryEstimateParam: DeliveryEstimatesRequestParameters,
  accessToken: string,
) {
  const postalCodeOverride = getStorage().getItem(
    'delivery_promise_postal_code',
  );
  const defaultPostalCodeOverride = getStorage().getItem(
    'delivery_promise_default_address',
  )?.zip;

  return deliveryPromiseAPIClient()(
    {
      ...deliveryEstimateParam,
      postalCode: postalCodeOverride,
      defaultPostalCode: defaultPostalCodeOverride,
    },
    accessToken,
  );
};

class DeliveryPromise extends HTMLElement implements CustomElement {
  target!: HTMLElement;
  connected = false;

  private _variantId: any =
    this.getAttribute(DeliveryPromiseAttribute.VariantId) ||
    hydratedVariantId();

  private _sellingPlanId: any = this.getAttribute(
    DeliveryPromiseAttribute.SellingPlanId,
  );

  constructor() {
    super();
    this.setUpShadowRoot();
  }

  static get observedAttributes() {
    return [...Object.values(DeliveryPromiseAttribute)];
  }

  attributeChangedCallback(name: string, _oldValue: any, newValue: any) {
    if (name === DeliveryPromiseAttribute.VariantId) {
      this.variantId = newValue;
    }

    if (name === DeliveryPromiseAttribute.SellingPlanId) {
      this.sellingPlanId = newValue;
    }
  }

  get variantId() {
    return this._variantId;
  }

  set variantId(value: any) {
    const valueChanged = this._variantId !== value;
    if (valueChanged) {
      this._variantId = value ? parseInt(value, 10) : null;
      if (this.connected) this.render();
    }
  }

  get sellingPlanId() {
    return this._sellingPlanId;
  }

  set sellingPlanId(value: any) {
    const valueChanged = this._sellingPlanId !== value;
    if (valueChanged) {
      this._sellingPlanId = value ? parseInt(value, 10) : null;
      if (this.connected) this.render();
    }
  }

  connectedCallback() {
    this.connected = true;
    this.render();
  }

  disconnectedCallback() {
    this.connected = false;
    // ensures graceful shutdown of the component, and removing all event listeners
    unmountComponentAtNode(this.target);
  }

  private setUpShadowRoot() {
    this.prepareShadowRoot();

    const styles = `#generic-promise-wc button { all: revert; position: relative; }`;

    if (this.ownerDocument?.adoptedStyleSheets) {
      this.ownerDocument.adoptedStyleSheets.push(
        (() => {
          const sheet = new CSSStyleSheet();
          sheet.replaceSync(styles);
          return sheet;
        })(),
      );
    } else {
      // Unsupported adoptedStyleSheets fallback, append the style to the head.
      const style = document.createElement('style');
      style.type = 'text/css';
      style.textContent = styles;
      this.ownerDocument.head.appendChild(style);
    }
  }

  private render() {
    const shadowRoot = this.shadowRoot as ShadowRoot;
    unmountComponentAtNode(this.target);
    if (module.hot) {
      this.prepareShadowRoot();
    }
    render(
      <App
        shadowRoot={shadowRoot}
        variantId={this.variantId}
        sellingPlanId={this.sellingPlanId}
      />,
      this.target,
    );
  }

  private prepareShadowRoot() {
    /**
     * Hot-reload ad-hoc solution for the Dev page
     */
    const shadowRoot =
      this.shadowRoot ??
      this.attachShadow({mode: 'open', delegatesFocus: true});
    shadowRoot.innerHTML = '';
    shadowRoot.append(...getStyleTags());

    this.target = document.createElement('div');
    shadowRoot.appendChild(this.target);
  }
}

function defineShopPromiseElement() {
  /**
   * Ad-hoc support for a Hot-reload on the Dev page
   */
  if (customElements.get(tag)) {
    // hot reload
    return document.querySelectorAll(tag).forEach((node) => {
      // Swap prototype of instance with new one
      Object.setPrototypeOf(node, DeliveryPromise.prototype);

      // re-renders
      (node as unknown as CustomElement).connectedCallback();
    });
  }

  customElements.define(tag, DeliveryPromise);
}

function prefetchDeliveryEstimates() {
  const fetchDeliveryEstimates =
    window.Shopify.ShopPromise.deliveryPromiseAPIClient;
  const variantId = hydratedVariantId();

  if (variantId) {
    const postalCode = getStorage().getItem('delivery_promise_postal_code');
    const defaultAddressPostalCode = getStorage().getItem(
      'delivery_promise_default_address',
    )?.zip;
    const {accessToken} = readShopifyFeatures();
    ShopPromisePerformance.measurePromise(
      'deliveryEstimateGraphQL',
      fetchDeliveryEstimates(
        {
          variantId,
          sellingPlanId: resolveSellingPlanId(),
          postalCode: postalCode ?? undefined,
          defaultAddressPostalCode,
        },
        accessToken,
      ).catch(noopErrorHandler),
    );

    ShopPromisePerformance.measurePromise(
      'resolveVariant',
      fetchProductDetails().catch(noopErrorHandler),
    );
  }
}

if (module.hot) {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const clientEmitter = require('webpack/hot/emitter');

  module.hot.accept();

  clientEmitter.on('webpackHotUpdate', defineShopPromiseElement);
}

prefetchDeliveryEstimates();

function initializeShopPromise() {
  trackDeliveryPromiseLoaded();
  if (!window.Shopify.ShopPromise.mounted) {
    ShopPromisePerformance.measureAsset('shopPromiseAsset');
    window.Shopify.ShopPromise.mounted = true;

    mountWebComponent();
  }

  defineShopPromiseElement();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeShopPromise);
} else {
  initializeShopPromise();
}

/**
 * Prevents sending unhandled rejections to the Bugsnag.
 */
function noopErrorHandler() {}
